import * as React from "react";
import { toast } from "react-toastify";
import StyledModal from "styled-react-modal";
import { useMappedState } from "redux-react-hook";
import _ from "lodash";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { ButtonLink } from "src/library/components/Link";
import { Modal } from "src/library/components/Modal";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import { IAction } from "src/library/components/Button/ActionButton";
import CustomerInfoSidebar from "src/core/customers/components/CustomerInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { ImportModal } from "src/core/shared/components/ImportModal";
import { usersTableColumns } from "src/core/shared/components/tables/usersTableColumns";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { useCustomer } from "src/core/customers/hooks";
import { PageProps } from "src/pages/Router/routes";
import { IStore } from "src/core/shared/store/index";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  ERPOdoo,
  ERPRJL,
  ERPSeedless,
  ERPUnity
} from "src/utils/constants";
import {
  ImportOrigin,
  ImportSettingType
} from "src/core/shared/api/importSetting.api";

interface UsersPageProps {
  customerId: string;
}

/**
 * Customer "Users" View
 *
 */
const CustomerUsersPage: React.ReactFCWithChildren<PageProps<unknown, UsersPageProps>> = props => {
  const {
    history, match, terminology
  } = props;

  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const { isLoading: loadingCustomer, customer } = useCustomer(customerId);
  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const [ showImportModal, setShowImportModal ] = React.useState(false);

  const { erp, importSettings } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    importSettings: global.settings?.importSettings
  }));

  const [ usersResponse, getUsers ] = useUserApi("LIST_USERS");
  const usersData = usersResponse.data;
  const [ deleteUserResponse, deleteUser ] = useUserApi("DELETE_USER");
  const [ suspendUserResponse, suspendUser ] = useUserApi("UPDATE_USER");
  const [ resendInviteResponse, resendInvite ] = useUserApi("RESEND_INVITE");
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const [ exportCustomerUserRes, exportCustomerUsers ] = useUserApi("EXPORT_CUSTOMER_USER");
  const pageFilters = { customerId };
  const checkPermission = useCheckUserPermission();

  const canCreateUsers = React.useMemo(() => {
    return checkPermission("create-user");
  }, [ checkPermission ]);

  const onFetchData = React.useCallback((filters: FilterParams<IUser>, pagination: PaginatedParams<IUser>) => {
    getUsers({
      pagination,
      filters
    });
  }, []);

  React.useEffect(() => {
    if (exportCustomerUserRes.complete) {
      toast.success("Users are being exported. Check the job queue for details");
    }

    if (exportCustomerUserRes.error) {
      toast.error(`Error exporting users - ${exportCustomerUserRes.error.message}`);
    }
  }, [ exportCustomerUserRes ]);

  React.useEffect(() => {
    if (deleteUserResponse.data && TableRef.current) {
      TableRef.current.reloadTable();
    }

    if (deleteUserResponse.error) {
      toast("Could not delete this user!");
    }
  }, [ deleteUserResponse ]);

  const resendInviteUserAction = (id: number) => {
    resendInvite({ id });
  };

  const editUserAction = (id: number) => {
    history.push(`/admin/customers/${customerId}/users/${id}/details`);
  };

  const deleteUserAction = (id: number) => {
    setModalData(id);
  };

  const deleteUserActionConfirm = () => {
    if (modalData) {
      deleteUser({ id: modalData });
      setModalData(null);
    }
  };

  const handleSuspendUser = (user: any) => {
    const updatedUser = {
      ...user,
      status: user.status === "active" ? "suspended" : "active"
    };

    suspendUser(updatedUser);
  };

  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ suspendUserResponse ]);

  const LikeToDoButtons = React.useCallback(() => {
    const buttons = [
      <ButtonLink
        key="new-user"
        to={`/admin/customers/${customerId}/users/create`}
      >
        {`Create new ${terminology.user.singular.text}`}
      </ButtonLink>,

      <Button
        key="export-customer-user"
        loading={exportCustomerUserRes.pending}
        onClick={() => {
          exportCustomerUsers({ customerIds: [ customerId ] });
        }}
      >
        {`Export ${terminology.user.plural.text}`}

      </Button>
    ];

    const hasUserCSVImportSetting = _.find(importSettings, is => is.type === ImportSettingType.User && is.origin === ImportOrigin.CSV && !is.options?.exportOnly);

    if (hasUserCSVImportSetting && (erp === ERPSeedless || erp === ERPRJL || erp === ERPUnity || erp === ERPOdoo)) {
      buttons.push((
        <Button
          key="import-users"
          onClick={() => setShowImportModal(true)}
        >
          {`Import ${terminology.user.plural.text}`}
        </Button>)
      );
    }

    return buttons;
  }, [
    customerId,
    erp,
    exportCustomerUserRes.pending,
    exportCustomerUsers,
    importSettings,
    terminology.user.plural.text,
    terminology.user.singular.text
  ]);

  React.useEffect(() => {
    if (resendInviteResponse.data) {
      toast.success("The invite has been sent");
    }

    if (resendInviteResponse.error) {
      toast.error(`Invite not sent ${terminology.user.singular.text} - ${resendInviteResponse.error.message}`);
    }
  }, [ resendInviteResponse, terminology.user.singular.text ]);

  return (
    <Layout
      {...props}
      title={customer ? `${customer.name} - ${terminology.user.plural.text}` : ""}
    >
      <MainContentWrapper
        fill
        Header={canCreateUsers && (
          <LikeToDo
            copy={`Setup a new user for this ${terminology.customer.singular.text}.`}
            buttons={LikeToDoButtons()}
          />
        )}
      >

        <StyledModal isOpen={showImportModal}>
          <ImportModal
            open={showImportModal}
            onDismiss={() => setShowImportModal(false)}
            type="user"
            terminology={props.terminology}
          />
        </StyledModal>

        <Table
          tableId={`customerId:${customerId}`}
          ref={TableRef}
          mainAction={row => editUserAction(row.original.id)}
          error={usersResponse.error}
          tableOf={terminology.user.plural.text}
          data={usersData ? usersData.result : []}
          loading={usersResponse.pending}
          onFetchData={onFetchData}
          totalRows={usersData ? usersData.count : 0}
          pages={usersData ? usersData.pages : 0}
          defaultFilters={pageFilters}
          columns={usersTableColumns({
            ActionButton: row => {
              const items: IAction[] = [
                {
                  title: "Edit",
                  action: () => editUserAction(row.original.id),
                  disabled: !checkPermission("amend-user")
                },
                {
                  title: `${row.original.status === "active" ? "Suspend" : "Activate"}`,
                  disabled: !checkPermission("amend-user"),
                  action: () => {
                    handleSuspendUser(row.original);
                  }
                },
                {
                  title: "Delete",
                  disabled: !checkPermission("delete-user"),
                  action: () => deleteUserAction(row.original.id)
                }
              ];

              if (row.original.lastLoggedIn === null) {
                items.push({
                  title: "Resend invite",
                  disabled: !checkPermission("create-user"),
                  action: () => resendInviteUserAction(row.original.id)
                });
              }

              return (
                <ActionButton
                  title="Actions"
                  items={items}
                />
              );
            }
          })}
        />
      </MainContentWrapper>

      <InfoBarWrapper>
        <CustomerInfoSidebar
          terminology={terminology}
          isLoading={loadingCustomer}
          data={customer}
        />
      </InfoBarWrapper>

      <Modal
        small
        actions={[
          {
            name: "Yes",
            action: deleteUserActionConfirm,
            $buttonType: "primary"
          },
          {
            name: "No",
            action: () => setModalData(null),
            $buttonType: "secondary"
          }
        ]}
        backgroundAction={() => setModalData(null)}
        body={`Deleting this ${terminology.user.singular.text} cannot be undone`}
        type="confirm"
        title="Are you sure"
        show={modalData !== null}
      />
    </Layout>
  );
};

export default CustomerUsersPage;
